<template>
  <v-dialog v-model="internalShowModal" max-width="600" @click:outside="closeModal">
    <v-card>
      <v-card-title
        class="headline d-flex justify-space-between align-center"
      >
        <div>Alert Details</div>
        <v-spacer />
        <v-select
          v-model="internalTorqueUnit"
          :items="torqueUnitOptions"
          dense
          width="100%"
          outlined
          hide-details
          class="
            torque-unit-select
            font-size-input
            border border-radius-md
            placeholder-lighter
            text-color-light
          "
          style="width: 150px"
        ></v-select>
        <v-btn
          icon
          @click="closeModal"
          class="ml-2"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider class="my-3"></v-divider>

      <v-card-text v-if="alert && !showScheduler && !workOrderProcessing && !workOrderComplete">
        <v-row>
          <v-col cols="6">
            <div class="metric">
              <div class="label">Status</div>
              <div class="value">
                <v-chip
                  :color="getStatusColor(alert.status)"
                  text-color="white"
                  small
                >
                  {{ alert.status }}
                </v-chip>
              </div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="metric">
              <div class="label">Timestamp</div>
              <div class="value">
                {{ formatTimestamp(alert.timestamp) }}
              </div>
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <v-row>
          <v-col cols="6">
            <div class="metric">
              <div class="label">Initial Torque</div>
              <div class="value">
                {{ displayTorque(alert.initialTorque) }}
                {{ internalTorqueUnit }}
              </div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="metric">
              <div class="label">Current Torque</div>
              <div class="value">
                {{ displayTorque(alert.currentTorque) }}
                {{ internalTorqueUnit }}
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            <div class="metric">
              <div class="label">Initial Angle</div>
              <div class="value">{{ alert.initialAngle }}°</div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="metric">
              <div class="label">Torque Angle Deviation</div>
              <div class="value">
                {{ alert.torqueAngleDeviation }}°
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            <div class="metric">
              <div class="label">Loosening Rate</div>
              <div class="value">
                {{ displayTorque(alert.looseningRate) }}
                {{ internalTorqueUnit }}/°
              </div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="metric">
              <div class="label">Torque Deviation</div>
              <div class="value">
                {{ displayTorque(alert.torqueDeviation) }}
                {{ internalTorqueUnit }}
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            <div class="metric">
              <div class="label">Battery Level</div>
              <div class="value">{{ alert.batteryLevel }}%</div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="metric">
              <div class="label">Temperature</div>
              <div class="value">{{ alert.temperature }}°F</div>
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <v-row>
          <v-col cols="12">
            <div class="metric risk">
              <div class="label">Risk Assessment</div>
              <div
                class="value"
                :class="getStatusClass(alert.status)"
              >
                {{ alert.riskMessage }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Schedule Inspection Form -->
      <v-card-text v-if="showScheduler">
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">Schedule Inspection</h3>
            <v-date-picker
              v-model="inspectionDate"
              :allowed-dates="allowedDates"
              class="mb-4 full-width-calendar"
              elevation="2"
              dark
              width="100%"
              color="primary"
            ></v-date-picker>
            
            <h4 class="mb-2 white--text">Available Time Slots</h4>
            <div class="time-slot-grid">
              <v-btn
                v-for="slot in availableTimeSlots"
                :key="slot.value"
                :color="inspectionTime === slot.value ? 'primary' : 'grey darken-3'"
                :disabled="slot.disabled"
                class="time-slot-btn mx-1 my-1"
                @click="inspectionTime = slot.value"
                :outlined="inspectionTime !== slot.value"
                dark
              >
                {{ slot.label }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Work Order Processing/Complete -->
      <v-card-text v-if="workOrderProcessing || workOrderComplete" class="text-center py-8">
        <div v-if="workOrderProcessing" class="work-order-processing">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
          <div class="mt-4 text-h6">Creating work order...</div>
          <div class="grey--text">Connecting to BuildOps</div>
        </div>
        
        <div v-if="workOrderComplete" class="work-order-complete">
          <v-icon color="success" size="64">mdi-check-circle</v-icon>
          <div class="mt-4 text-h6">Work Order Created!</div>
          <div class="grey--text">Work Order #{{ workOrderNumber }} has been successfully created in BuildOps</div>
        </div>
      </v-card-text>

      <!-- Inspection Confirmation -->
      <v-card-text v-if="inspectionComplete" class="text-center py-8">
        <v-icon color="success" size="64">mdi-calendar-check</v-icon>
        <div class="mt-4 text-h6">Inspection Scheduled!</div>
        <div class="grey--text">Thank you for your request. We will confirm your inspection for {{ formatInspectionDate }} shortly.</div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        
        <template v-if="!showScheduler && !workOrderProcessing && !workOrderComplete && !inspectionComplete">
          <v-btn color="secondary" outlined @click="scheduleInspection">
            Schedule Inspection
          </v-btn>
          <v-btn color="secondary" dark @click="createWorkOrder">
            Create Work Order
          </v-btn>
        </template>
        
        <template v-if="showScheduler">
          <v-btn color="error" text @click="cancelScheduling">
            Cancel
          </v-btn>
          <v-btn 
            color="secondary" 
            style="background-color: #000000;"
            :disabled="!inspectionDate || !inspectionTime"
            @click="confirmInspection"
          >
            Confirm Inspection
          </v-btn>
        </template>
        
        <template v-if="workOrderComplete || inspectionComplete">
          <v-btn dark color="secondary" @click="closeModal">
            Close
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { DateTime } from "luxon";
import { 
  updateSingleObjectToFirebase,
  createWorkOrder,
  createInspection
} from "@/services/firebase-helpers";

export default {
  name: "AlertDetailsModal",
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    alert: {
      type: Object,
      default: null
    },
    torqueUnit: {
      type: String,
      default: "ft-lbs"
    },
    deviceId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      torqueUnitOptions: [
        { text: "Newton meters (Nm)", value: "Nm" },
        { text: "Foot pounds (ft-lbs)", value: "ft-lbs" },
      ],
      // Scheduler state
      showScheduler: false,
      inspectionDate: null,
      inspectionTime: null,
      inspectionComplete: false,
      
      // Work order state
      workOrderProcessing: false,
      workOrderComplete: false,
      workOrderNumber: null,
    };
  },
  computed: {
    internalShowModal: {
      get() {
        return this.showModal;
      },
      set(value) {
        this.$emit('update:showModal', value);
      }
    },
    internalTorqueUnit: {
      get() {
        return this.torqueUnit;
      },
      set(value) {
        this.$emit('update:torqueUnit', value);
      }
    },
    // Conversion factor for Nm to lb-ft
    conversionFactor() {
      return 0.737562;
    },
    
    // Available time slots
    availableTimeSlots() {
      const slots = [];
      const disabledSlots = this.getDisabledTimeSlots();
      
      for (let hour = 9; hour <= 16; hour++) {
        const amPm = hour < 12 ? 'AM' : 'PM';
        const hour12 = hour > 12 ? hour - 12 : hour;
        const timeValue = `${hour}:00`;
        const timeLabel = `${hour12}:00 ${amPm}`;
        
        slots.push({
          value: timeValue,
          label: timeLabel,
          disabled: disabledSlots.includes(timeValue)
        });
      }
      
      return slots;
    },
    
    // Formatted inspection date and time for confirmation message
    formatInspectionDate() {
      if (!this.inspectionDate || !this.inspectionTime) return '';
      
      const date = DateTime.fromISO(this.inspectionDate);
      const formattedDate = date.toFormat('MMMM d, yyyy');
      
      // Convert time from 24hr "14:00" to "2:00 PM"
      const [hour] = this.inspectionTime.split(':');
      const hour12 = hour > 12 ? hour - 12 : hour;
      const amPm = hour < 12 ? 'AM' : 'PM';
      
      return `${formattedDate} at ${hour12}:00 ${amPm}`;
    }
  },
  methods: {
    formatTimestamp(timestamp) {
      return DateTime.fromISO(timestamp).toFormat("MMM d, yyyy HH:mm");
    },

    getStatusColor(status) {
      switch (status) {
        case "Critical":
          return "red";
        case "Warning":
          return "amber";
        case "Secure":
          return "green";
        default:
          return "grey";
      }
    },

    getStatusClass(status) {
      switch (status) {
        case "Critical":
          return "red--text";
        case "Warning":
          return "amber--text";
        case "Secure":
          return "green--text";
        default:
          return "";
      }
    },

    displayTorque(value) {
      if (value === undefined || value === null) return "0.0";
      
      const numValue = Number(value);
      if (isNaN(numValue)) return "0.0";
      
      if (this.internalTorqueUnit === "ft-lbs") {
        return (numValue * this.conversionFactor).toFixed(1);
      }
      return numValue.toFixed(1);
    },

    closeModal() {
      this.resetModalState();
      this.$emit('update:showModal', false);
      this.$emit('close');
    },

    scheduleInspection() {
      this.showScheduler = true;
    },
    
    cancelScheduling() {
      this.showScheduler = false;
      this.inspectionDate = null;
      this.inspectionTime = null;
    },
    
    confirmInspection() {
      // Create inspection data
      const inspectionData = {
        title: `Scheduled Inspection - Device ${this.deviceId}`,
        description: `Inspection scheduled for alert on device ${this.deviceId}. ${this.alert.riskMessage}`,
        deviceId: this.deviceId,
        locationId: 'location_001',
        alertId: this.alert.id,
        scheduledDate: this.inspectionDate,
        scheduledTime: this.inspectionTime,
        inspector: 'Inspection Team',
        estimatedDuration: 1.5,
        notes: `Inspection requested for ${this.alert.status} alert`,
        alertDetails: {
          torqueAngleDeviation: this.alert.torqueAngleDeviation,
          looseningRate: this.alert.looseningRate,
          torqueDeviation: this.alert.torqueDeviation,
          batteryLevel: this.alert.batteryLevel,
          temperature: this.alert.temperature
        }
      };
      
      // Create inspection in Firebase
      createInspection(inspectionData)
        .then((result) => {
          this.showScheduler = false;
          this.inspectionComplete = true;
          console.log('Inspection scheduled successfully:', result);
        })
        .catch((error) => {
          console.error('Failed to schedule inspection:', error);
          this.showScheduler = false;
          this.inspectionComplete = true;
        });
    },

    createWorkOrder() {
      this.workOrderProcessing = true;
      
      // Create work order data
      const workOrderData = {
        title: `Critical Alert Response - ${this.alert.status}`,
        description: `Work order created for alert on device ${this.deviceId}. ${this.alert.riskMessage}`,
        deviceId: this.deviceId,
        locationId: 'location_001',
        alertId: this.alert.id,
        priority: this.alert.status === 'Critical' ? 'High' : 'Medium',
        assignedTo: 'Maintenance Team',
        estimatedHours: this.alert.status === 'Critical' ? 4 : 2,
        alertDetails: {
          torqueAngleDeviation: this.alert.torqueAngleDeviation,
          looseningRate: this.alert.looseningRate,
          torqueDeviation: this.alert.torqueDeviation,
          batteryLevel: this.alert.batteryLevel,
          temperature: this.alert.temperature
        }
      };
      
      // Create work order in Firebase
      createWorkOrder(workOrderData)
        .then((result) => {
          this.workOrderProcessing = false;
          this.workOrderComplete = true;
          this.workOrderNumber = result.workOrderNumber;
          console.log('Work order created successfully:', result);
        })
        .catch((error) => {
          console.error('Failed to create work order:', error);
          this.workOrderProcessing = false;
          this.workOrderComplete = true;
          this.workOrderNumber = "WO-" + Math.floor(100000 + Math.random() * 900000);
        });
    },
    
    resetModalState() {
      this.showScheduler = false;
      this.inspectionDate = null;
      this.inspectionTime = null;
      this.inspectionComplete = false;
      this.workOrderProcessing = false;
      this.workOrderComplete = false;
      this.workOrderNumber = null;
    },
    
    // Helper methods for the scheduler
    allowedDates(date) {
      const day = new Date(date).getDay();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      return day !== 0 && day !== 6 && new Date(date) >= today;
    },
    
    getDisabledTimeSlots() {
      if (!this.inspectionDate) return [];
      
      const dateHash = new Date(this.inspectionDate).getDate();
      const disabledCount = 3 + (dateHash % 4);
      
      const allSlots = Array.from({length: 8}, (_, i) => `${i+9}:00`);
      const shuffled = [...allSlots].sort(() => 0.5 - Math.random());
      
      return shuffled.slice(0, disabledCount);
    },
  },
  watch: {
    showModal(newVal) {
      if (!newVal) {
        this.resetModalState();
      }
    },
    inspectionDate() {
      this.inspectionTime = null;
    }
  }
};
</script>

<style scoped>
.metric {
  margin-bottom: 12px;
}

.metric .label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}

.metric .value {
  font-size: 16px;
  font-weight: 500;
}

.metric.risk .value {
  font-size: 14px;
  margin-top: 4px;
}

.time-slot-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 15px;
}

.work-order-processing,
.work-order-complete {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.full-width-calendar {
  width: 100% !important;
  background-color: #1e1e1e !important;
}

.time-slot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
  margin-top: 15px;
}

.time-slot-btn {
  height: 44px !important;
}

:deep(.v-date-picker-table) {
  color: white !important;
}

:deep(.v-date-picker-header) {
  color: white !important;
}

:deep(.v-date-picker-table__events) {
  color: white !important;
}
</style> 